import forge from 'node-forge'
import { isVaultV2 } from './format'

/**
 * **정방향 변환 — v1(AES-CBC) 금고 값을 v2(AES-256-GCM)로 올린다** (PR 7-a).
 *
 * 설계: `backend/docs/design/vault_forward_sweep/`
 *
 * 🛑 **`reverseSweep.js` 와 대칭이지만 한 가지가 다르다 — 왕복 검증이 여기에만 있다.**
 * 역방향은 롤백 도구라 이미 열린 값을 되돌리지만, 정방향은 **사용자의 유일한 사본**을
 * 다시 쓴다. 잘못 변환한 값을 저장하면 원본 평문이 영구히 사라지고 백업이 없다
 * (로드맵 §7.2 — 2026-06-09 dev user_key 오염 사고가 같은 구조였다).
 *
 * 🛑 **왕복 검증이 막는 것과 못 막는 것** (spec §6.1). `encrypt` 와 `decrypt` 가 같은
 * `rsKeyObj.rsKey` 를 쓰므로 **잘못된 키는 왕복으로 잡히지 않는다.** 왕복이 잡는 것은
 * base64 경계·UTF-8·헤더 조립 같은 **인코더/디코더 구현 불일치**다.
 *
 * 🛑 **그리고 `finish()` 도 확정적이지 않다** — node-forge 1.4.0 까지 PKCS#7 검사(`node-forge/lib/cipherModes.js` 의 unpad)는
 * 잘못된 키의 약 25% 를 통과시켰고(74.5% 만 막음, 3000회), 1.4.1 부터는 약 0.4% 다(20,000회 측정 0.34~0.49%).
 * 이 함수는 통과 뒤 UTF-8 해석까지 거쳐 변환에 이르는 비율이 1.4.0 에서 약 20% 다. 1.4.1 은 복호+UTF-8 해석만 흉내 낸 측정에서, 1.4.2 는 이 함수의 실제 경로 측정에서 0건이었다(15바이트 평문).
 * AES-CBC 에 MAC 이 없어 런타임 확인 수단이 없다 — 그것이 GCM 으로 옮기는 이유다.
 * **실질 방어선은 호출부(`sweepRunner.js`)의 "한 행이라도 복호 실패면 전체 중단"(설계 D10)** 이고, 그것을 완화하면 위의 변환에 이르는 비율이 곧 손실률이 된다.
 *
 * 🛑 **순회·커서·API 호출은 여기 없다** — `sweepRunner.js` 가 한다. 이 파일은 값 하나만 다룬다.
 */

/**
 * 왕복 검증 실패. **일반 복호 실패와 구분한다** — 전자는 인코더 결함(코드 문제)이고
 * 후자는 키·데이터 문제라 운영자가 볼 다음 행동이 다르다.
 */
export class RoundTripError extends Error {
  constructor(message) {
    super(message)
    this.name = 'RoundTripError'
    this.code = 'ROUND_TRIP_MISMATCH'
  }
}

/**
 * v1 blob 을 v2 로 다시 쓴다.
 *
 * @param {string} blobBase64 DB 에 저장된 그대로의 값 (base64 — 대상 4개 컬럼 전부 base64 다)
 * @param {{encrypt: (plain: string) => Promise<string>, decrypt: (b64: string) => Promise<string>}} rsKeyObj
 *        `services/vaultV2/resourceKey.js` 가 만든 객체. `encrypt` 는 **서버가 지시한 포맷**으로
 *        갈린다(`pUtils.js:1388` — 카나리가 꺼져 있으면 v1 이 나온다).
 * @returns {Promise<string|null>} v2 blob 의 base64, 또는 **대상이 아니면 `null`**
 *
 * **복호 실패는 던진다** — 실패한 값을 그대로 재암호화하면 그것이 이 마이그레이션에서
 * 되돌릴 수 없는 유일한 실패다.
 */
export const rewriteV1ToV2 = async (blobBase64, rsKeyObj) => {
  // 이미 v2 면 아무것도 하지 않는다. **복호조차 하지 않는다** — 매 언락마다 전량을 다시 훑는
  // 구조(설계 D6)라 여기서 걸러야 2회차가 무동작이 된다.
  if (isVaultV2(forge.util.decode64(blobBase64))) return null

  const plain = await rsKeyObj.decrypt(blobBase64)
  const nextBase64 = await rsKeyObj.encrypt(plain)

  // 카나리가 꺼진 워크스페이스면 `encrypt` 가 v1 을 낸다 → 바꿀 이유가 없다.
  // 예외가 아니라 "대상 아님"으로 강등한다 (판별 실패는 기존 경로로 — planning-rules fe#6).
  if (!isVaultV2(forge.util.decode64(nextBase64))) return null

  // 🔴 왕복 검증 — 여기를 통과하지 못한 값은 **절대 반환하지 않는다**.
  const roundTrip = await rsKeyObj.decrypt(nextBase64)
  if (roundTrip !== plain) {
    throw new RoundTripError('v2 re-encryption did not round-trip to the original plaintext')
  }

  return nextBase64
}

export default rewriteV1ToV2
